/**
 * Pantalla principal de gestión de álbumes para la versión web.
 * Muestra álbumes permanentes, álbumes compartidos (to-share) y álbumes accesibles (shared-with-me).
 * Adaptada desde la versión móvil de AlbumsListManagerPage.
 */

import React, { useState, useCallback } from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { PermanentAlbums } from '../../components/album-sistem/my-albums';
import { ShareAlbumDialog } from '../../components/album-sistem/common';

/**
 * Componente AlbumesListManagerPage
 * 
 * Página principal de gestión de álbumes.
 * Muestra álbumes permanentes, álbumes compartidos (to-share) y álbumes accesibles (shared-with-me).
 */
export default function AlbumesListManagerPage() {
  const navigate = useNavigate();
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedAlbumForShare, setSelectedAlbumForShare] = useState(null);
  const [emptyAlbums, setEmptyAlbums] = useState({
    permanent: {},
    shared: {}
  });

  // Datos simulados - reemplazar con queries reales cuando estén disponibles
  const toShareAlbums = [];
  const accessibleAlbums = [];

  /**
   * Callback para actualizar el estado de vacío de un álbum
   */
  const handleAlbumEmptyStatus = useCallback((albumIdentifier, isEmpty) => {
    setEmptyAlbums(prev => {
      const isSharedAlbum = albumIdentifier.includes('-') && albumIdentifier.length > 20;

      if (isSharedAlbum) {
        return {
          ...prev,
          shared: {
            ...prev.shared,
            [albumIdentifier]: isEmpty
          }
        };
      } else {
        return {
          ...prev,
          permanent: {
            ...prev.permanent,
            [albumIdentifier]: isEmpty
          }
        };
      }
    });
  }, []);

  /**
   * Verifica si hay álbumes vacíos
   */
  const hasEmptyAlbums = () => {
    const hasEmptyPermanentAlbum = Object.values(emptyAlbums.permanent).some(isEmpty => isEmpty);
    const hasEmptySharedAlbum = toShareAlbums.some(album => {
      const imageCount = album.image_count || 0;
      return imageCount === 0;
    });
    return hasEmptyPermanentAlbum || hasEmptySharedAlbum;
  };

  /**
   * Manejo de compartir álbum
   */
  const handleShareAlbum = (album) => {
    setSelectedAlbumForShare(album);
    setShowShareDialog(true);
  };

  /**
   * Manejo de crear álbum
   */
  const handleCreateAlbum = (name, description, selectedImages) => {
    console.log('Crear álbum:', { name, description, selectedImages });
    // Implementar lógica de creación cuando esté disponible
  };

  /**
   * Manejo de editar álbum
   */
  const handleEditAlbum = (albumId, newName) => {
    console.log('Editar álbum:', { albumId, newName });
    // Implementar lógica de edición cuando esté disponible
  };

  /**
   * Manejo de eliminar álbum
   */
  const handleDeleteAlbum = (albumId) => {
    console.log('Eliminar álbum:', albumId);
    // Implementar lógica de eliminación cuando esté disponible
  };

  return (
    <div className="w-full h-20 border-b border-neutral-800 bg-neutral-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4">
        <div className="flex items-center">
          <button 
            onClick={() => navigate('/mi-cuenta')}
            className="p-4 px-6 text-white hover:text-gray-300 transition-colors"
          >
            <FiArrowLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-white">
            Álbumes
          </h2>
        </div>
      </div>

      {/* Contenido principal */}
      <div className=''>
        {/* Sección 1: Mis fotos - Álbumes permanentes */}
        <div className='p-4'>
          <PermanentAlbums 
            indiscreto
            onEmptyStatusChange={handleAlbumEmptyStatus}
          />
        </div>

        {/* Sección 2: Mis Albumes - Álbumes to-share (deshabilitado por ahora) */}
        {false && (
          <div className="mt-8">
            <h3 className="text-white text-base font-semibold mb-3">Mis Álbumes</h3>
            <div className="text-gray-400 text-sm">
              Funcionalidad de álbumes compartidos por implementar
            </div>
          </div>
        )}

        {/* Sección 3: Compartidos - Álbumes accesibles (deshabilitado por ahora) */}
        {false && (
          <div className="mt-8 p-4">
            <h3 className="text-white text-base font-semibold mb-3">Compartidos</h3>
            <div className="text-gray-400 text-sm">
              Funcionalidad de álbumes compartidos conmigo por implementar
            </div>
          </div>
        )}
      </div>

      {/* ShareAlbumDialog */}
      <ShareAlbumDialog
        visible={showShareDialog}
        onClose={() => {
          setShowShareDialog(false);
          setSelectedAlbumForShare(null);
        }}
        albumId={selectedAlbumForShare?.id}
        albumName={selectedAlbumForShare?.name}
      />
    </div>
  );
}
